import React, { useEffect, useRef, useState } from "react"
import { graphql } from "gatsby"
import { useInView } from "react-intersection-observer"
import { ScrollTrigger } from "gsap/ScrollTrigger"
// import Img from "gatsby-image/withIEPolyfill"
import { Meta, PageCircles } from "@shared"
import {
  DemoForm,
  Footer,
  Header,
  WorkflowSteps,
  Banner,
  TechSpecs,
  CTAModule,
  GenericGrid,
  ScrollTextSection,
  MobileDemoSection
} from "@components/sections"

import { CONSTANTS } from "@data"
import { featureFlag, css, useSassUnitValues } from "@jsUtils"

import Styles from "@pageStyles/exoworks.module.scss"
import gsap from "gsap"

const { THEMES, COLORS, BREAKPOINTS, PAGE_TRACKER_KEYS, PAGE_TRACKER_VALUES } = CONSTANTS

const MOBILE_BREAKPOINT = useSassUnitValues(BREAKPOINTS.MOBILE)

const RESIZE_DEBOUNCE = 250

// const log = logTool(true)

/**
 * Exo Works Page
 * @param {object} props - react props
 * @todo hook up meta data with query
 */
// eslint-disable-next-line no-unused-vars
const ExoWorks = props => {
  const { data } = props
  const [mobileLayout, setMobileLayout] = useState(false)
  const { contentfulPageMeta, contentfulExoWorks, contentfulCrmEventBasedModal } = data
  const demoRef = useRef()
  const mentorRef = useRef()
  const [progress, setProgress] = useState(0)
  const [scrollTrigger, setScrollTrigger] = useState()
  const { ref, inView } = useInView({
    /* Optional options */
    threshold: 0
  })
  const { bottomBanner, techSpecs, techSpecPage, slug } = contentfulExoWorks
  const { isModalEnabledOnSite } = contentfulCrmEventBasedModal

  const [heroVideoLoaded, setHeroVideoLoaded] = useState(false)

  // declare animation timeline here, because it will be referenced
  // in both the mount and resize useEffect hooks
  let tl

  useEffect(() => {
    const element = mentorRef.current
    if (element) {
      const matchMedia = {}
      matchMedia[`(min-width: ${MOBILE_BREAKPOINT}px)`] = () => {
        tl = new gsap.timeline({
          scrollTrigger: {
            trigger: element,
            pin: true,
            scrub: 1,
            end: `+=${window.innerHeight * 2}`,
            onUpdate: self => {
              setProgress(self.progress)
            }
          }
        })
        setScrollTrigger(tl)
      }
      ScrollTrigger.matchMedia(matchMedia)
    }
    return () => {
      tl?.kill()

      // NOTE: not sure if this is needed anymore
      // was this an attempt to fix the "Workflow" section resizing bug?
      // "mobileLayout" isn't in the dependency array, so this effect doesn't run on resize
      if (element) {
        const parent = element.parentNode
        if (parent.classList.contains("pin-spacer")) {
          const grandparent = parent?.parentNode
          grandparent.insertBefore(element, parent)
          try {
            parent?.remove()
          } catch {
            console.warn("Failed to Remove Wrapper from Exo Works Pinned Section")
          }
        }
      }
    }
  }, [mentorRef])

  const handleResize = () => {
    if (window.innerWidth < MOBILE_BREAKPOINT && !mobileLayout) {
      setMobileLayout(true)

      // reset the animation timeline to the beginning
      tl?.progress(0)

      // refresh the ScrollTrigger to fix sizing issues
      ScrollTrigger.refresh()
    } else if (window.innerWidth >= MOBILE_BREAKPOINT && mobileLayout) {
      setMobileLayout(false)
    }
  }

  let resizeTimeout

  const requestResize = () => {
    clearTimeout(resizeTimeout)
    resizeTimeout = setTimeout(handleResize, RESIZE_DEBOUNCE)
  }

  useEffect(() => {
    // call the actual resize handler here
    handleResize()

    // bind the debounced resize handler to the event
    window.addEventListener("resize", requestResize)

    return () => {
      window.removeEventListener("resize", requestResize)
    }
  })

  return (
    <>
      <Meta
        base={contentfulPageMeta}
        page={contentfulExoWorks}
        hubSpotObj={{
          contentTypeKey: PAGE_TRACKER_KEYS.PRODUCT_PAGES,
          contentTypeValue: PAGE_TRACKER_VALUES.WEBSITE_PAGE
        }}
      />
      <Header theme={THEMES.DARK} pageColor={COLORS.DARK} />
      <DemoForm color={COLORS.FLURO} buttonColor={COLORS.WORKS} ref={demoRef} inView={inView} />
      {contentfulExoWorks.heroModule && (
        <GenericGrid
          data={contentfulExoWorks.heroModule}
          theme={THEMES.DARK}
          color={COLORS.WORKS}
          imgLayout="right"
          videoName="exo_works_1_hero"
          videoLoaded={() => setHeroVideoLoaded(true)}
          showTitle
          isLoop
          isTM
        />
      )}

      {contentfulExoWorks.secondaryModule && (
        <GenericGrid
          data={contentfulExoWorks.secondaryModule}
          theme={THEMES.TIN}
          color={COLORS.WORKS}
          imgLayout="right"
          extraClasses={{
            image: Styles.generic2Img
          }}
        >
          {heroVideoLoaded && (
            <PageCircles
              className={Styles.pageCircles}
              settings={{
                align: "right",
                color: COLORS.WORKS_TO_LIGHTBLUE,
                // fromTop: "calc(0.3vh + 0.6em)",
                size: "large",
                orbit: false,
                hideRing: true,
                extraClasses: {
                  wrapper: Styles.heroCircleWrapper,
                  circle: Styles.heroCircle2
                }
              }}
            />
          )}
        </GenericGrid>
      )}
      {contentfulExoWorks.workflowSteps && (
        <WorkflowSteps
          data={contentfulExoWorks.workflowSteps}
          theme={THEMES.TIN}
          color={COLORS.WORKS}
        />
      )}
      {contentfulExoWorks.qaModule && (
        <GenericGrid
          data={contentfulExoWorks.qaModule}
          theme={THEMES.TIN}
          color={COLORS.WORKS}
          imgLayout="left"
          videoName="exo_works_2_ipad"
          isLoop
          extraClasses={{
            wrapper: Styles.genericQAwrapper
          }}
        >
          <PageCircles
            className={css(Styles.pageCircles, Styles.qaSet)}
            settings={{
              items: [
                {
                  color: COLORS.WORKS_TO_LIGHTBLUE,
                  size: "small",
                  orbit: false,
                  hideRing: true,
                  extraClasses: {
                    wrapper: css(Styles.qaCircleWrapper),
                    circle: css(Styles.qaCircle),
                    ready: css(Styles.qaCircleReady)
                  }
                },
                {
                  color: COLORS.WORKS_TO_LIGHTBLUE,
                  size: "small",
                  orbit: false,
                  hideRing: true,
                  extraClasses: {
                    wrapper: css(Styles.qaCircleWrapper, Styles.wrapper2),
                    circle: css(Styles.qaCircle, Styles.circle2),
                    ready: css(Styles.qaCircleReady2)
                  }
                },
                {
                  color: COLORS.WORKS_TO_LIGHTBLUE,
                  size: "small",
                  orbit: false,
                  hideRing: true,
                  extraClasses: {
                    wrapper: css(Styles.qaCircleWrapper, Styles.wrapper3),
                    circle: css(Styles.qaCircle, Styles.circle3),
                    ready: css(Styles.qaCircleReady3)
                  }
                }
              ]
            }}
          />
        </GenericGrid>
      )}
      {contentfulExoWorks.swipeTextA && contentfulExoWorks.swipeTextB ? (
        <ScrollTextSection
          theme={THEMES.TIN}
          swipeTextA={contentfulExoWorks.swipeTextA}
          swipeTextB={contentfulExoWorks.swipeTextB}
        />
      ) : null}
      {contentfulExoWorks.mobileDemoSection && (
        <MobileDemoSection data={contentfulExoWorks.mobileDemoSection} theme={THEMES.TIN} />
      )}
      <Banner
        data={bottomBanner}
        extraClasses={{
          headline: Styles.bannerHeadline,
          description: Styles.bannerDescription,
          contentItem: Styles.bannerContentItem,
          overlay: Styles.bannerOverlay,
          image: Styles.bannerImage
        }}
      />

      {contentfulExoWorks.mentoringModule && (
        <div ref={mentorRef} className={css(mobileLayout && Styles.mobileMentoring)}>
          <GenericGrid
            className={Styles.mentoring}
            data={contentfulExoWorks.mentoringModule}
            theme={THEMES.DARK}
            color={COLORS.WORKS}
            videoControlled={!mobileLayout}
            videoProgress={progress}
            imgLayout="center"
            videoName="exo_works_3_insights"
          />
        </div>
      )}
      {contentfulExoWorks.safetyModule && (
        <GenericGrid
          data={contentfulExoWorks.safetyModule}
          theme={THEMES.TIN}
          color={COLORS.WORKS}
          imgLayout="left"
        />
      )}
      {contentfulExoWorks.techSpecsBanner ? (
        <CTAModule
          theme={THEMES[contentfulExoWorks.techSpecsBanner.theme]}
          color={COLORS[contentfulExoWorks.techSpecsBanner.color]}
          headline={contentfulExoWorks.techSpecsBanner.headline}
          subhead={contentfulExoWorks.techSpecsBanner.subHeadline}
          ctaCopy={contentfulExoWorks.techSpecsBanner.cta}
          hasCircles={contentfulExoWorks.techSpecsBanner.hasCircles}
          ctaLink={contentfulExoWorks.techSpecsBanner.url}
          // reducedHeadline
        />
      ) : null}
      <div ref={ref} />
      {contentfulExoWorks.demoBanner ? (
        <CTAModule
          theme={THEMES[contentfulExoWorks.demoBanner.theme]}
          color={COLORS[contentfulExoWorks.demoBanner.color]}
          headline={contentfulExoWorks.demoBanner.headline}
          subhead={contentfulExoWorks.demoBanner.subHeadline}
          ctaCopy={contentfulExoWorks.demoBanner.cta}
          hasCircles={contentfulExoWorks.demoBanner.hasCircles}
          ctaLink={contentfulExoWorks.demoBanner.url}
          onClick={() => demoRef.current.clickFromOutside()}
          inverseSymbol
          isBtn
        />
      ) : null}
      {featureFlag("exoWorksTechSpecs") && (
        <TechSpecs
          color={COLORS.WORKS}
          theme={THEMES.LIGHT}
          data={techSpecs}
          detailPage={`/${slug}/${techSpecPage.slug}`}
        />
      )}
      {/* {isModalEnabledOnSite ? (
        <EmailModal data={contentfulCrmEventBasedModal} theme={THEMES.DARK} color={COLORS.FLURO} />
      ) : null} */}
      <Footer />
      <div className={Styles.disclaimer}>
        *Product features may not be available for sale or subscription in all markets.
      </div>
    </>
  )
}

export default ExoWorks

/**
 * pageQuery for Exo Works
 */
export const pageQuery = graphql`
  {
    contentfulPageMeta(id: { eq: "423babd8-0278-5b2e-85f6-77fb7ee55bee" }) {
      ...GlobalMetaData
    }
    contentfulCrmEventBasedModal {
      heroImage {
        title
        fluid(quality: 100, maxWidth: 1920) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      supportingText
      heroText
      isModalEnabledOnSite
    }
    contentfulExoWorks {
      pageMeta {
        ...PageMetaData
      }
      title
      # headline
      # subhead
      slug
      # headerCopy {
      #   headerCopy
      # }
      workflowSteps {
        eyebrow
        headline
        videoId
        workflowsteps
      }
      techSpecsBanner {
        subHeadline
        headline
        theme
        color
        cta
        hasCircles
        url
      }
      demoBanner {
        subHeadline
        headline
        theme
        color
        cta
        hasCircles
        url
      }
      heroModule {
        id
        title
        eyebrow
        headline
        description {
          childMarkdownRemark {
            html
          }
        }
        image {
          fluid(quality: 100, maxWidth: 1920) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          title
        }
        imagePosition
      }
      secondaryModule {
        id
        title
        eyebrow
        headline
        description {
          childMarkdownRemark {
            html
          }
        }
        image {
          fluid(quality: 100, maxWidth: 1920) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          title
        }
        imagePosition
      }
      qaModule {
        id
        title
        eyebrow
        headline
        description {
          childMarkdownRemark {
            html
          }
        }
        image {
          fluid(quality: 100, maxWidth: 1920) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          title
        }
        imagePosition
      }
      safetyModule {
        id
        title
        eyebrow
        headline
        description {
          childMarkdownRemark {
            html
          }
        }
        image {
          fluid(quality: 100, maxWidth: 1920) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          title
        }
        imagePosition
      }
      mentoringModule {
        id
        title
        eyebrow
        headline
        description {
          childMarkdownRemark {
            html
          }
        }
        image {
          fluid(quality: 100, maxWidth: 1920) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          title
        }
        imagePosition
      }
      # heroImages {
      #   fluid(quality: 100, maxWidth: 1920) {
      #     ...GatsbyContentfulFluid_withWebp_noBase64
      #   }
      #   title
      # }
      # headerImages {
      #   fluid(quality: 100, maxWidth: 1920) {
      #     ...GatsbyContentfulFluid_withWebp_noBase64
      #   }
      #   title
      # }
      # topBanner {
      #   ...BannerSection
      # }
      # features {
      #   ...WorksFeatureSection
      # }
      # workflow {
      #   id
      #   title
      #   headline
      # }
      # workFlowImage {
      #   title
      #   fluid(quality: 100, maxWidth: 1920) {
      #     ...GatsbyContentfulFluid_withWebp_noBase64
      #   }
      # }
      # benefits {
      #   id
      #   title
      #   description {
      #     childMarkdownRemark {
      #       html
      #     }
      #   }
      #   image {
      #     title
      #     fluid(quality: 100, maxWidth: 1920) {
      #       ...GatsbyContentfulFluid_withWebp_noBase64
      #     }
      #   }
      # }
      # techSpecPage {
      #   slug
      # }
      # techSpecs {
      #   ...TechSpecsSection
      # }
      bottomBanner {
        ...BannerSection
      }
      swipeTextA
      swipeTextB
      mobileDemoSection {
        id
        title
        headline
        description {
          childMarkdownRemark {
            html
          }
        }
        image {
          fluid(quality: 100, maxWidth: 1920) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          title
        }
        imagePosition
      }
    }
  }
`
